import { useState } from 'react';
import type { DiscountReasonCode, DiscountType } from '@salonomy/types';
import { Button, Field, Modal, SegmentedControl, Select, TextArea, TextInput } from '@salonomy/ui';
import { useApplyDiscount } from '../../data/discounts.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { poundsToPence } from './money.js';
import { DISCOUNT_REASON_LABELS, DISCOUNT_REASON_ORDER } from './reasonLabels.js';

/**
 * WS9 §3 — apply a checkout DISCOUNT with a REQUIRED reason code (surface b). Percent or fixed; never a
 * silent override (brief non-negotiable) — the form enforces a reason code, and a free-text note when the
 * reason is "other". The ACTOR is the authenticated staff member (server-resolved, never sent from here).
 *
 * HONESTY: the till POSTs intent (type + value + base + reason); the API computes and returns the
 * integer-pence `discountPence`, which is what the caller records — the UI never computes the reduction
 * locally. Apply is idempotent on a per-intent key minted once on submit; the button is disabled while
 * pending so a double-tap can't apply twice, and a 422 (e.g. missing note) surfaces faithfully.
 *
 * NOTE: a voucher redemption is NOT a discount — it's a tender (see VoucherRedeemModal). This surface is
 * only for genuine price reductions that the salon wants reported by reason.
 */
export function DiscountModal({
  appointmentId,
  clientId,
  basePence,
  onApplied,
  onClose,
}: {
  appointmentId: string;
  clientId: string | null;
  /** The supply gross the discount reduces (a server number — the basket's outstanding amount). */
  basePence: number;
  /** Called with the server-computed reduction (pence) so the till records the discount line. */
  onApplied: (discountPence: number) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const apply = useApplyDiscount();

  const [type, setType] = useState<DiscountType>('percent');
  const [percent, setPercent] = useState('10');
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState<DiscountReasonCode>('loyalty');
  const [note, setNote] = useState('');

  const noteRequired = reasonCode === 'other';
  const valueBps = type === 'percent' ? Math.round((Number(percent) || 0) * 100) : null;
  const valuePence = type === 'fixed' ? poundsToPence(amount) : null;
  const valueOk =
    type === 'percent'
      ? valueBps != null && valueBps >= 1 && valueBps <= 10000
      : valuePence != null && valuePence > 0;
  const canApply = valueOk && (!noteRequired || note.trim().length > 0);

  async function submit() {
    if (!canApply) return;
    try {
      // Mint the idempotency key once per intent; the button is disabled while pending so a double-tap
      // can't apply a second discount, and the API is idempotent on this key.
      const discount = await apply.mutateAsync({
        appointmentId,
        clientId: clientId ?? undefined,
        type,
        valueBps: type === 'percent' ? (valueBps ?? undefined) : undefined,
        valuePence: type === 'fixed' ? (valuePence ?? undefined) : undefined,
        basePence,
        reasonCode,
        reasonNote: note.trim() ? note.trim() : undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      // Record the server-computed reduction — never a locally recomputed number.
      onApplied(discount.discountPence);
      toast.success(`Discount applied · ${money(discount.discountPence)} off`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not apply the discount');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply discount"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canApply || apply.isPending}>
            {apply.isPending ? 'Applying…' : 'Apply discount'}
          </Button>
        </>
      }
    >
      <div className="till__line muted" style={{ marginBottom: 12 }}>
        <span>Discount applies to</span>
        <span className="mono">{money(basePence)}</span>
      </div>

      <Field label="Type">
        <SegmentedControl<DiscountType>
          options={[
            { value: 'percent', label: 'Percent' },
            { value: 'fixed', label: 'Fixed £' },
          ]}
          value={type}
          onChange={setType}
        />
      </Field>

      {type === 'percent' ? (
        <Field label="Percent off (%)" hint="1–100%.">
          <TextInput
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="10"
          />
        </Field>
      ) : (
        <Field label="Amount off (£)" hint={`Up to ${money(basePence)}.`}>
          <TextInput
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </Field>
      )}

      <Field label="Reason" hint="Every discount is recorded with a reason — no silent overrides.">
        <Select
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value as DiscountReasonCode)}
        >
          {DISCOUNT_REASON_ORDER.map((code) => (
            <option key={code} value={code}>
              {DISCOUNT_REASON_LABELS[code]}
            </option>
          ))}
        </Select>
      </Field>

      {noteRequired ? (
        <Field label="Note" hint="Required for an “other” reason (it's reportable).">
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why this discount was given"
            rows={2}
          />
        </Field>
      ) : null}

      <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
        The exact reduction is calculated by the till server and shown on the receipt; it&rsquo;s
        recorded against you and this reason for reporting.
      </div>
    </Modal>
  );
}
